import { useQuery } from '@tanstack/react-query';
import { captureEvent, Severity } from '@sentry/browser';

import { findDomain, getEmailDomain } from 'helpers/email';

type DNSAnswer = {
  TTL: number;
  data: string;
  name: string;
  type: number;
};

type DNSResponse = {
  Answer: DNSAnswer[];
};

const fetchDomainData = async (email: string): Promise<string | null> => {
  const emailDomain = getEmailDomain(email);
  if (!emailDomain) {
    return null;
  }

  try {
    const response = await fetch(`https://dns.google/resolve?name=${emailDomain}&type=MX`);
    const dnsData: DNSResponse = await response.json();
    const domainData = dnsData?.Answer?.map((answer) => answer.data);

    if (!dnsData?.Answer) {
      captureEvent({
        message: `User attempted to sign up with a potentially incorrect domain`,
        level: Severity.Warning,
        extra: {
          emailDomain,
        },
      });
    }

    return findDomain(domainData);
  } catch (e) {
    return null;
  }
};

type UseGetEmailDomain = {
  domain: string | null;
  isFetching: boolean;
};

export const useGetEmailDomain = (email: string): UseGetEmailDomain => {
  const { data, isFetching } = useQuery<string, string>(
    ['get-email-domain', email],
    () => fetchDomainData(email),
    {
      refetchOnWindowFocus: false,
      enabled: !!email,
      initialData: null,
    }
  );

  return {
    domain: data,
    isFetching,
  };
};
